import { useCallback, useEffect, useRef, useState } from 'react';
import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

/**
 * Opens the device camera and runs MediaPipe FaceLandmarker over the video.
 * The heavy result (478 landmarks + blendshapes + transformation matrix) is
 * handed to a caller-supplied `onFrame`; nothing is drawn or stored here.
 *
 * Camera selection:
 *   - The FRONT (user/selfie) camera is the default/priority when present (the
 *     client captures their own face). We request it with { ideal } so devices
 *     without a front cam fall back gracefully.
 *   - When more than one camera exists, `canSwitch` is true and `switchCamera()`
 *     flips between front/back without reloading the model.
 *   - `facingMode` reflects the ACTUAL camera in use ('user' | 'environment'),
 *     so the UI can mirror only the front (selfie) camera.
 *
 * Order matters for UX: we acquire the CAMERA FIRST (so it visibly turns on and
 * the permission prompt appears immediately), then load the model while the
 * preview is already showing.
 *
 * Status lifecycle: 'idle' -> 'requesting' -> 'loading' -> 'ready'
 *   error branches: 'insecure' | 'denied' | 'no-camera' | 'error'
 *
 * Resilient to React StrictMode's double mount/unmount via a generation counter.
 * The <video> element must already be mounted before start() runs.
 *
 * Model assets load CDN-FIRST with a SAME-ORIGIN (host) FALLBACK: the app tries
 * the public CDN, and only if that fails (blocked/offline/CDN down) does it
 * retry from files served by this PWA's own host under /mediapipe/. Populate
 * Model assets load per VITE_MEDIAPIPE_SOURCE (default 'auto' = CDN-first with
 * a SAME-ORIGIN host fallback). Set it to 'host' to load ONLY from this PWA's
 * own host under /mediapipe/ — for deployments where the device can reach only
 * our host and has no public internet. Populate the host files with
 * `npm run fetch-mediapipe` (see public/mediapipe/README).
 */

// Public CDN (primary).
const CDN_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm';
const CDN_MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

// Same-origin fallback, served from this app's public/ folder (respects the
// Vite base path). Collapse any accidental double slashes (BASE_URL ends in /).
const BASE = import.meta.env.BASE_URL || '/';
const LOCAL_WASM_BASE = `${BASE}mediapipe/wasm`.replace(/([^:]\/)\/+/g, '$1');
const LOCAL_MODEL_URL = `${BASE}mediapipe/face_landmarker.task`.replace(/([^:]\/)\/+/g, '$1');

const CDN = { label: 'cdn', wasm: CDN_WASM_BASE, model: CDN_MODEL_URL };
const HOST = { label: 'host', wasm: LOCAL_WASM_BASE, model: LOCAL_MODEL_URL };

/**
 * Where to load the WASM runtime + model from, set by VITE_MEDIAPIPE_SOURCE:
 *   'auto'       (default) → CDN first, host fallback
 *   'host'                 → HOST ONLY (use when the device can reach ONLY our
 *                            host, e.g. no public internet — no CDN attempt)
 *   'host-first'           → host first, CDN fallback
 *   'cdn'                  → CDN only
 * Returns the ordered list of sources to try.
 */
function modelSources() {
  switch ((import.meta.env.VITE_MEDIAPIPE_SOURCE || 'auto').toLowerCase()) {
    case 'host': return [HOST];
    case 'host-first': return [HOST, CDN];
    case 'cdn': return [CDN];
    default: return [CDN, HOST]; // 'auto'
  }
}

/** Build a FaceLandmarker from a given WASM base + model URL. */
async function createLandmarker(wasmBase, modelUrl) {
  const fileset = await FilesetResolver.forVisionTasks(wasmBase);
  return FaceLandmarker.createFromOptions(fileset, {
    baseOptions: { modelAssetPath: modelUrl, delegate: 'GPU' },
    runningMode: 'VIDEO',
    numFaces: 1,
    outputFaceBlendshapes: true,
    outputFacialTransformationMatrixes: true,
  });
}

const VIDEO_CONSTRAINTS = { width: { ideal: 1280 }, height: { ideal: 720 } };

/** Read the real facing of a stream's video track ('user' | 'environment'). */
function detectFacing(stream) {
  const s = stream?.getVideoTracks?.()[0]?.getSettings?.() || {};
  // Laptops usually report no facingMode → treat as a front/selfie cam.
  return s.facingMode === 'environment' ? 'environment' : 'user';
}

export function useFaceLandmarker({ onFrame, active = true } = {}) {
  const videoRef = useRef(null);
  const landmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(0);
  const lastVideoTimeRef = useRef(-1);
  const genRef = useRef(0);              // cancels stale async work on start/stop
  const requestedFacingRef = useRef('user'); // what we ASK for (front/selfie first)
  const switchingRef = useRef(false);    // guards re-entrant switches

  const onFrameRef = useRef(onFrame);
  useEffect(() => { onFrameRef.current = onFrame; }, [onFrame]);

  const [status, setStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('user'); // ACTUAL cam in use
  const [canSwitch, setCanSwitch] = useState(false);

  const loop = useCallback(() => {
    const video = videoRef.current;
    const landmarker = landmarkerRef.current;
    if (video && landmarker && video.readyState >= 2 && video.currentTime !== lastVideoTimeRef.current) {
      lastVideoTimeRef.current = video.currentTime;
      try {
        const result = landmarker.detectForVideo(video, performance.now());
        onFrameRef.current?.(result, video);
      } catch {
        /* transient detect errors during teardown are non-fatal */
      }
    }
    rafRef.current = requestAnimationFrame(loop);
  }, []);

  const stop = useCallback(() => {
    genRef.current += 1; // invalidate any in-flight start
    cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    lastVideoTimeRef.current = -1;
  }, []);

  /** Attach a freshly-acquired stream to the <video> and start playing. */
  const attachStream = useCallback(async (stream) => {
    const video = videoRef.current;
    if (!video) return false;
    streamRef.current = stream;
    video.srcObject = stream;
    video.setAttribute('playsinline', 'true');
    video.muted = true;
    await video.play().catch(() => {});
    setFacingMode(detectFacing(stream));
    return true;
  }, []);

  const start = useCallback(async () => {
    const myGen = (genRef.current += 1);
    setError(null);

    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
      setStatus('insecure');
      return;
    }

    try {
      // 1) Camera first — back camera as priority (ideal → graceful fallback).
      setStatus('requesting');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: requestedFacingRef.current }, ...VIDEO_CONSTRAINTS },
        audio: false,
      });
      if (genRef.current !== myGen) { stream.getTracks().forEach((t) => t.stop()); return; }
      if (!(await attachStream(stream)) || genRef.current !== myGen) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      // Multiple cameras? Labels need permission, but the COUNT is available now.
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        setCanSwitch(devices.filter((d) => d.kind === 'videoinput').length > 1);
      } catch { /* enumeration optional */ }

      // 2) Model — load while the preview is already on screen. Try each
      //    configured source in order (see VITE_MEDIAPIPE_SOURCE / modelSources).
      setStatus('loading');
      if (!landmarkerRef.current) {
        const sources = modelSources();
        let lastErr = null;
        for (const src of sources) {
          try {
            landmarkerRef.current = await createLandmarker(src.wasm, src.model);
            break;
          } catch (e) {
            if (genRef.current !== myGen) return;
            lastErr = e;
            console.warn(`[face] model load from ${src.label} failed`, e);
          }
        }
        if (!landmarkerRef.current) throw (lastErr || new Error('model load failed'));
      }
      if (genRef.current !== myGen) return;

      setStatus('ready');
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(loop);
    } catch (err) {
      if (genRef.current !== myGen) return;
      const name = err?.name || '';
      if (name === 'NotAllowedError' || name === 'SecurityError') setStatus('denied');
      else if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'NotReadableError') setStatus('no-camera');
      else { setStatus('error'); setError(err); }
      stop();
    }
  }, [loop, stop, attachStream]);

  /** Flip between front/back. Keeps the model + detection loop running. */
  const switchCamera = useCallback(async () => {
    if (switchingRef.current || !streamRef.current) return;
    switchingRef.current = true;
    const prev = requestedFacingRef.current;
    const next = facingMode === 'environment' ? 'user' : 'environment';

    // Most phones won't open two camera streams at once, so release first.
    streamRef.current.getTracks().forEach((t) => t.stop());
    streamRef.current = null;

    const acquire = async (facing) => navigator.mediaDevices.getUserMedia({
      video: { facingMode: { ideal: facing }, ...VIDEO_CONSTRAINTS },
      audio: false,
    });

    try {
      requestedFacingRef.current = next;
      await attachStream(await acquire(next));
    } catch {
      // Couldn't switch — fall back to the previous camera so we're not dark.
      try {
        requestedFacingRef.current = prev;
        await attachStream(await acquire(prev));
      } catch { /* give up; status stays 'ready' but preview may be blank */ }
    } finally {
      switchingRef.current = false;
    }
  }, [facingMode, attachStream]);

  useEffect(() => {
    if (active) start();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  useEffect(() => () => {
    try { landmarkerRef.current?.close?.(); } catch { /* noop */ }
    landmarkerRef.current = null;
  }, []);

  return { videoRef, status, error, start, stop, facingMode, canSwitch, switchCamera };
}
